import clsx from "clsx";
import React, { useEffect, useState } from "react";

import { Coordinates, Reason } from "../../../types/location";
import { EXCEPT_BUILDING, NEAR_DISTANCE } from "../../../types/building";

import { MANAGER_ROLE } from "../../../services/memberServices";

import { getDistanceFormat } from "../../../libs/distanceFormat";

import { useUserContext } from "../../../contexts/userContext";

import {
  getNearBuilding,
  NearBuildingItem,
  NearBuildingServiceType,
} from "../../../services/buildingServices";

import useInfiniteScroll, {
  INFINITE_CODE,
  InfiniteScrollCallbackArgs,
} from "../../../hooks/useInfiniteScroll";
import useSimplePopup from "../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import { CurrentPosition } from "../../../pages/Center";
import CommonButton from "../../Button";
import PrimaryButton from "../../Button/Primary";
import CommonInput from "../../Input/CommonInput";
import CommonRadio from "../../Input/Radio";

import ZoomSearchIcon from "../../../svgs/icons/ZoomSearchIcon";

type Props = {
  onChange: (building: NearBuildingItem) => void;
  onClose: () => void;
  defaultBuilding?: NearBuildingItem;
  coordinate?: Coordinates;
  onCallback?: () => void;
  type?: NearBuildingServiceType;
  reason: Reason;
  isVoc?: boolean;
};

export default function FindBuilding({
  onChange,
  onClose,
  defaultBuilding,
  coordinate,
  onCallback,
  type,
  reason,
}: Props) {
  const [user] = useUserContext();
  const [search, setSearch] = useState("");
  const [nearBuilding, setNearBuilding] = useState<NearBuildingItem[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<
    NearBuildingItem | undefined
  >(defaultBuilding);
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasCalled = React.useRef<boolean>(false);
  const PAGE_SIZE = 10;
  const SCROLL_LOAD_START_PAGE = 2;
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const loadingComponent = useFullScreenLoading({
    isLoading,
  });
  const isAdmin = user.roles?.includes(MANAGER_ROLE.ADMIN);
  const isWorkLocationManager = user.roles?.includes(
    MANAGER_ROLE.FREE_LOCATION
  );

  const searchBuilding = async (
    args: InfiniteScrollCallbackArgs & { searchText?: string }
  ) => {
    let returnCount = INFINITE_CODE.ERROR;
    const { searchText, ...remainArgs } = args;
    setIsLoading(true);
    hasCalled.current = true;

    try {
      const result = await getNearBuilding({
        ...(searchText
          ? { buildingName: searchText, address: searchText }
          : {}),
        ...remainArgs,
        ...coordinate,
        serviceType: type,
        reason,
      });
      const newItems =
        searchText || isAdmin || isWorkLocationManager
          ? result.data.items
          : result.data.items.filter((item) => item.distance < NEAR_DISTANCE);
      if (
        searchText &&
        args.page === 1 &&
        newItems.filter((item) => item.distance < NEAR_DISTANCE).length === 0 &&
        !isAdmin &&
        !isWorkLocationManager
      ) {
        open("1km 내에 건물이 없습니다.");
      }
      if (remainArgs.page === 1) {
        setNearBuilding(newItems);
      } else {
        setNearBuilding((prev) => [...prev, ...newItems]);
      }
      returnCount = newItems.length;
      if (args.page === 1 && newItems.length === 0) {
        open("검색 결과가 없습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.message) {
        open(e.response.data.message);
      } else {
        open("건물 검색에 실패했습니다.\n재시도해주세요.");
      }
    } finally {
      setIsLoading(false);
    }
    return returnCount;
  };

  const [, { resetScroll }] = useInfiniteScroll({
    callback: async (args) => {
      return searchBuilding({ ...args, searchText: search });
    },
    isLoading: isLoading && !hasCalled.current,
    target: scrollRef,
  });

  useEffect(() => {
    try {
      onCallback?.();
    } catch (e) {
      // setError(JSON.stringify(e));
    }
  }, []);

  return (
    <div className="flex flex-col gap-2">
      {loadingComponent}
      <div className="px-5">
        <CommonInput
          placeholder="건물명 또는 장소를 입력해주세요"
          className="p-4 text-base w-full bg-brand-neutral-05"
          postfix={{
            content: (
              <div
                onClick={() => {
                  resetScroll();
                  searchBuilding({
                    searchText: search,
                    page: SCROLL_LOAD_START_PAGE - 1,
                    pageSize: PAGE_SIZE,
                  });
                }}
                className="opacity-60"
                data-testid="search-icon-button"
              >
                <ZoomSearchIcon />
              </div>
            ),
            className: "px-3",
          }}
          value={search}
          onChange={(value: string) => setSearch(value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              resetScroll();
              searchBuilding({
                searchText: search,
                page: SCROLL_LOAD_START_PAGE - 1,
                pageSize: PAGE_SIZE,
              });
            }
          }}
          variant="building"
        />
        <CommonButton
          className="w-full h-[52px] opacity-60"
          variant="transparent"
          onClick={() => {
            resetScroll();
            setSearch("");
            searchBuilding({
              searchText: "",
              page: SCROLL_LOAD_START_PAGE - 1,
              pageSize: PAGE_SIZE,
            });
          }}
        >
          <CurrentPosition />
          현위치로 근처 건물 보기
        </CommonButton>
      </div>
      <div className="max-h-[40vh] overflow-auto" ref={scrollRef}>
        {nearBuilding.map((item, index) => (
          <BuildingLocationItem
            key={index}
            item={item}
            onClick={(item) => {
              if (item.buildingId === selectedBuilding?.buildingId) return;

              if (
                item.distance > NEAR_DISTANCE &&
                !EXCEPT_BUILDING.includes(item.buildingId) &&
                !isAdmin &&
                !isWorkLocationManager
              ) {
                open("1km 이내의 건물을 선택해주세요.");
              } else {
                setSelectedBuilding(item);
              }
            }}
            radioValue={selectedBuilding?.buildingId === item.buildingId}
          />
        ))}
      </div>
      <PrimaryButton
        className="text-white h-[50px] w-full flex justify-center items-center text-base font-medium"
        onClick={() => {
          if (!selectedBuilding) {
            return open("건물을 선택해주세요.");
          }
          onChange(selectedBuilding);
          setSearch("");
          setNearBuilding([]);
          setSelectedBuilding(defaultBuilding ?? undefined);
          onClose();
        }}
      >
        확인
      </PrimaryButton>

      {simplePopupComponent}
    </div>
  );
}

export type BuildingLocationItemProps = {
  item: NearBuildingItem;
  onClick: (item: NearBuildingItem) => void;
  radioValue?: boolean;
  className?: string;
};

export const BuildingLocationItem = ({
  item,
  onClick,
  radioValue,
  className,
}: BuildingLocationItemProps) => {
  return (
    <div
      className={clsx([
        "flex gap-2 px-5 py-4 border-t border-gray-200 items-center",
        className,
      ])}
      key={item.buildingId}
      onClick={() => onClick(item)}
    >
      {radioValue !== undefined && <CommonRadio value={radioValue} />}
      <div className="flex flex-col gap-1 w-full">
        <div className="text-base font-bold">{item.buildingName}</div>
        <div className="text-sm text-gray-400 flex gap-1 items-center">
          <div className="min-w-4">
            <FindBuildingLocation />
          </div>
          {item.address}
        </div>
      </div>
      <div className="text-sm text-gray-400">
        {getDistanceFormat(item.distance ?? 0)}
      </div>
    </div>
  );
};

export const FindBuildingLocation = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.97846 14.5275L4.13511 10.8128C3.3936 10.0813 2.89202 9.15451 2.69328 8.14863C2.49454 7.14275 2.60747 6.10254 3.01792 5.15844C3.42838 4.21435 4.11807 3.40838 5.00054 2.84162C5.883 2.27486 6.91894 1.97252 7.97847 1.97252C9.03799 1.97252 10.0739 2.27486 10.9564 2.84162C11.8389 3.40838 12.5286 4.21435 12.939 5.15844C13.3495 6.10254 13.4624 7.14275 13.2637 8.14863C13.0649 9.15451 12.5633 10.0813 11.8218 10.8128L7.97846 14.5275Z"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.1744 7.43432C10.1744 8.64715 9.19121 9.63035 7.97837 9.63035C6.76554 9.63035 5.78235 8.64715 5.78235 7.43432C5.78235 6.22149 6.76554 5.2383 7.97837 5.2383C9.19121 5.2383 10.1744 6.22149 10.1744 7.43432Z"
      fill="white"
      stroke="white"
      strokeWidth="1.75"
    />
  </svg>
);
